import { cloneElement, forwardRef } from "react";

import { useDelayedLoading } from "@oneleet/utils/src/hooks";

import { type VariantProps, cva } from "class-variance-authority";
import { twMerge } from "tailwind-merge";

import { Loader } from "@oneleet/components/src/atoms";

const DEFAULT_VARIANT = "plain";
const DEFAULT_COLOR = "gray";
const DEFAULT_SIZE = "medium";
const DEFAULT_IS_DISABLED = false;

const iconButton = cva(
  "flex items-center justify-center rounded-md text-sm font-semibold",
  {
    variants: {
      variant: {
        solid: "",
        soft: "",
        outlined: "",
        dashed: "",
        plain: "",
      },
      color: {
        brand: "",
        gray: "",
        red: "",
        green: "",
      },
      size: {
        tiny: "p-1",
        small: "p-1",
        medium: "p-1.5",
        large: "p-2",
        huge: "p-2.5",
      },
      disabled: {
        true: "opacity-50",
        false: "",
      },
    },
    compoundVariants: [
      {
        variant: "solid",
        color: "brand",
        className: "bg-brand-500 hover:enabled:bg-brand-600 text-white",
      },
      {
        variant: "soft",
        color: "brand",
        className: "bg-brand-50 text-brand-500 hover:enabled:bg-brand-100",
      },
      {
        variant: "outlined",
        color: "brand",
        className:
          "text-brand-500 hover:enabled:bg-brand-50 ring-brand-300 bg-transparent ring-1 ring-inset",
      },
      {
        variant: "dashed",
        color: "brand",
        className:
          "text-brand-500 hover:enabled:bg-brand-50 border-brand-300 border-2 border-dashed bg-transparent",
      },
      {
        variant: "plain",
        color: "brand",
        className: "text-brand-500 hover:enabled:bg-brand-50 bg-transparent",
      },
      {
        variant: "solid",
        color: "gray",
        className: "bg-gray-500 text-white hover:enabled:bg-gray-400",
      },
      {
        variant: "soft",
        color: "gray",
        className: "bg-gray-100 text-gray-600 hover:enabled:bg-gray-200",
      },
      {
        variant: "outlined",
        color: "gray",
        className:
          "bg-transparent text-gray-600 ring-1 ring-inset ring-gray-300 hover:enabled:bg-gray-100",
      },
      {
        variant: "dashed",
        color: "gray",
        className:
          "border-2 border-dashed border-gray-300 bg-transparent text-gray-600 hover:enabled:bg-gray-100",
      },
      {
        variant: "plain",
        color: "gray",
        className: "bg-transparent text-gray-600 hover:enabled:bg-gray-100",
      },
      {
        variant: "solid",
        color: "red",
        className: "bg-red-600 text-white hover:enabled:bg-red-500",
      },
      {
        variant: "soft",
        color: "red",
        className: "bg-red-50 text-red-600 hover:enabled:bg-red-100",
      },
      {
        variant: "outlined",
        color: "red",
        className:
          "bg-transparent text-red-600 ring-1 ring-inset ring-red-300 hover:enabled:bg-red-50",
      },
      {
        variant: "dashed",
        color: "red",
        className:
          "border-2 border-dashed border-red-300 bg-transparent text-red-600 hover:enabled:bg-red-50",
      },
      {
        variant: "plain",
        color: "red",
        className: "bg-transparent text-red-600 hover:enabled:bg-red-50",
      },
      {
        variant: "solid",
        color: "green",
        className: "bg-green-500 text-white hover:enabled:bg-green-400",
      },
      {
        variant: "soft",
        color: "green",
        className: "bg-green-50 text-green-600 hover:enabled:bg-green-100",
      },
      {
        variant: "outlined",
        color: "green",
        className:
          "bg-transparent text-green-600 ring-1 ring-inset ring-green-300 hover:enabled:bg-green-50",
      },
      {
        variant: "dashed",
        color: "green",
        className:
          "border-2 border-dashed border-green-300 bg-transparent text-green-600 hover:enabled:bg-green-50",
      },
      {
        variant: "plain",
        color: "green",
        className: "bg-transparent text-green-600 hover:enabled:bg-green-50",
      },
    ],
    defaultVariants: {
      variant: DEFAULT_VARIANT,
      color: DEFAULT_COLOR,
      size: DEFAULT_SIZE,
      disabled: DEFAULT_IS_DISABLED,
    },
  },
);

const iconClassNames = cva("h-5 w-5", {
  variants: {
    size: {
      tiny: "h-4 w-4",
      small: "",
      medium: "",
      large: "",
      huge: "",
    },
  },
});

const loader = cva("h-5 w-5", {
  variants: {
    variant: {
      solid: "",
      soft: "",
      outlined: "",
      dashed: "",
      plain: "",
    },
    color: {
      brand: "",
      gray: "",
      red: "",
      green: "",
    },
    size: {
      tiny: "h-4 w-4",
      small: "",
      medium: "",
      large: "",
      huge: "",
    },
  },
  compoundVariants: [
    {
      variant: "solid",
      color: "brand",
      className: "text-brand-300 fill-white",
    },
    {
      variant: ["soft", "outlined", "dashed", "plain"],
      color: "brand",
      className: "text-brand-300 fill-brand-500",
    },
    {
      variant: "solid",
      color: "gray",
      className: "fill-white text-gray-300",
    },
    {
      variant: ["soft", "outlined", "dashed", "plain"],
      color: "gray",
      className: "fill-gray-600 text-gray-300",
    },
    {
      variant: "solid",
      color: "red",
      className: "fill-white text-red-300",
    },
    {
      variant: ["soft", "outlined", "dashed", "plain"],
      color: "red",
      className: "fill-red-600 text-red-300",
    },
    {
      variant: "solid",
      color: "green",
      className: "fill-white text-green-300",
    },
    {
      variant: ["soft", "outlined", "dashed", "plain"],
      color: "green",
      className: "fill-green-600 text-green-300",
    },
  ],
});

type ButtonVariantProps = VariantProps<typeof iconButton>;

interface IconProps {
  className?: string;
}

export interface IconButtonProps
  extends ButtonVariantProps,
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color" | "disabled"> {
  icon: React.ReactElement<IconProps>;
  /**
   * Automatically disables the button when true
   */
  isLoading?: boolean;
  className?: string;
  classNames?: {
    loader?: string;
  };
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      variant = DEFAULT_VARIANT,
      color = DEFAULT_COLOR,
      size = DEFAULT_SIZE,
      icon,
      type = "button",
      disabled = DEFAULT_IS_DISABLED,
      isLoading = false,
      className,
      classNames,
      onClick,
      ...rest
    },
    ref,
  ) => {
    const styledIcon = icon
      ? cloneElement(icon, {
          className: twMerge(iconClassNames({ size }), icon.props.className),
        })
      : null;

    const showLoading = useDelayedLoading({ isLoading });

    const isDisabled = disabled || showLoading;

    return (
      <button
        type={type}
        className={twMerge(
          iconButton({
            variant,
            color,
            size,
            disabled: isDisabled,
            className,
          }),
        )}
        disabled={isDisabled}
        onClick={onClick}
        ref={ref}
        {...rest}
      >
        {!showLoading && styledIcon}
        {showLoading && (
          <Loader
            classNames={{
              svg: twMerge(
                loader({ variant, color, size, className: classNames?.loader }),
              ),
            }}
          />
        )}
      </button>
    );
  },
);

IconButton.displayName = "IconButton";

export default IconButton;
